/**
 * UX-PLAN Pillar F — per-session mirror for the three chat-send toggles
 * (`orchestrateWithAll`, `createNewProject`, `selfImprovement`).
 *
 * The toggles used to be three independent `@state()` booleans on the
 * app shell. That made them global: switching sessions carried the
 * previous value forward. The plan calls for these to be persisted in
 * `UiSettings` and resolved per session, mirroring the
 * `chatComposerModeBySession` and `fleetDefaultQueueModeBySession`
 * patterns already in place.
 *
 * Uses the existing `FleetChatSendOptions` type from
 * `fleet-chat-send-options.ts` — the field name there is
 * `orchestrateWithAll`, not `orchestrateAll`. Single record per session
 * key keeps the on-disk shape compact and avoids three parallel map
 * types. Pure functions; no DOM, no Lit.
 */

import type { FleetChatSendOptions } from "./fleet-chat-send-options.ts";

export type { FleetChatSendOptions };

export const DEFAULT_CHAT_SEND_OPTIONS: FleetChatSendOptions = {
  orchestrateWithAll: false,
  createNewProject: false,
  selfImprovement: false,
};

export type ChatSendOptionsBySession = Record<string, FleetChatSendOptions>;

/** Resolve the send options for a given session, falling back to the
 *  passed-in defaults. Empty/whitespace keys return the defaults. */
export function resolveChatSendOptions(
  bySession: ChatSendOptionsBySession | undefined | null,
  fallback: FleetChatSendOptions,
  sessionKey: string | null | undefined,
): FleetChatSendOptions {
  if (!bySession || typeof bySession !== "object") {
    return fallback;
  }
  const trimmed = (sessionKey ?? "").trim();
  if (!trimmed) {
    return fallback;
  }
  const entry = bySession[trimmed];
  if (!entry || typeof entry !== "object") {
    return fallback;
  }
  return {
    orchestrateWithAll:
      typeof entry.orchestrateWithAll === "boolean"
        ? entry.orchestrateWithAll
        : fallback.orchestrateWithAll,
    createNewProject:
      typeof entry.createNewProject === "boolean"
        ? entry.createNewProject
        : fallback.createNewProject,
    selfImprovement:
      typeof entry.selfImprovement === "boolean" ? entry.selfImprovement : fallback.selfImprovement,
  };
}

/** Update a single field on the per-session map; preserves all other
 *  sessions and other fields on the same session. */
export function setChatSendOptionForSession<K extends keyof FleetChatSendOptions>(
  bySession: ChatSendOptionsBySession | undefined | null,
  sessionKey: string,
  field: K,
  value: FleetChatSendOptions[K],
): ChatSendOptionsBySession {
  const next: ChatSendOptionsBySession = { ...(bySession ?? {}) };
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return next;
  }
  const existing = next[trimmed] ?? DEFAULT_CHAT_SEND_OPTIONS;
  next[trimmed] = { ...existing, [field]: value };
  return next;
}

/** Migrate / normalize a stored per-session map. Invalid entries are
 *  silently dropped; valid entries are merged with
 *  `DEFAULT_CHAT_SEND_OPTIONS` so a partial save cannot produce `NaN`
 *  downstream. */
export function normalizeChatSendOptionsBySession(raw: unknown): ChatSendOptionsBySession {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const out: ChatSendOptionsBySession = {};
  for (const [key, value] of Object.entries(raw)) {
    const trimmed = key.trim();
    if (!trimmed || !value || typeof value !== "object") {
      continue;
    }
    const entry = value as Record<string, unknown>;
    out[trimmed] = {
      orchestrateWithAll:
        typeof entry.orchestrateWithAll === "boolean"
          ? entry.orchestrateWithAll
          : DEFAULT_CHAT_SEND_OPTIONS.orchestrateWithAll,
      createNewProject:
        typeof entry.createNewProject === "boolean"
          ? entry.createNewProject
          : DEFAULT_CHAT_SEND_OPTIONS.createNewProject,
      selfImprovement:
        typeof entry.selfImprovement === "boolean"
          ? entry.selfImprovement
          : DEFAULT_CHAT_SEND_OPTIONS.selfImprovement,
    };
  }
  return out;
}
